import { h, createContext } from 'preact'
import { useErrorBoundary, useReducer } from 'preact/hooks'
import {
  Accept,
  Save,
  Settings,
  Toggle,
  View
} from '../reducers'
import {
  Actions,
  Framework,
  Languages,
  ManagePartners,
  ManageSettings,
  Views
} from '../../enums'
import { getViewLanguage } from '../localize'
import { bodyScroll } from '../../../helpers/scroll'
import { AC } from '../../../ac/AC'
import { TCF } from '../../../../cmp/tcf/TCF'
import { queryParamHas } from '../../../../helpers/getQueryParam'
import { onPrivacyPolicyPage } from '../../../../wrapper'

// For easier debugging and access to tcModel
// @ts-ignore
// window.tcModel = TCF.tcModel 

export const MV_DEFAULT_COLOR = '#41a4a9'

let initialView: Views | null = null
const hasTcfCookie = TCF.cookie.value
const hasACString = AC.value
const isGdprTest = queryParamHas('test', 'gdpr')
const isPrivacyPolicyPage = onPrivacyPolicyPage(window.$mediavine.web.model)

/** Always display the UI when privacy policy page, 
 * "?test=gdpr" query param, no preexisting cookie,
 * or no AC (additional consent)
 */
if (isPrivacyPolicyPage || isGdprTest || !hasTcfCookie || !hasACString) {
  initialView = Views.default
  // Prevents body from scrolling while modal is displayed
  bodyScroll(false)
  TCF.consentDoesApply({ displayUi: true })
  // Users without a consent string get *all* legitimate interests turned "on"
  !hasTcfCookie && TCF.tcModel.setAllPurposeLegitimateInterests()
  !hasTcfCookie && TCF.tcModel.setAllVendorLegitimateInterests()
} else {
  TCF.consentDoesApply({ displayUi: false })
}

const { model } = window.$mediavine.web
const initialState: IAppState = {
  acceptedAcPartners: AC.parseString() || {},
  color: model.custom_cmp_color || MV_DEFAULT_COLOR,
  header: model.custom_cmp_header,
  language: getViewLanguage(),
  privacyPolicy: model.privacy_policy_link,
  title: model.title,
  view: initialView,
}

const Context = createContext<StateContext>({
  state: initialState,
  dispatch: (): void => {}
})

const reducerCallback = (prevState: IAppState, action: IAction) => {
  switch (action.type) {
    case Actions.accept: {
      return Accept.reducer(prevState)
    }
    case Actions.save: {
      return Save.reducer(prevState)
    }
    case Actions.settings: {
      return Settings.reducer(prevState)
    }
    case Actions.toggle: {
      return Toggle.reducer(prevState, action)
    }
    case Actions.view: {
      return View.reducer(prevState, action)
    }
    default: {
      return prevState
    }
  }
}

const StateProvider = ({ children }) => {
  const [error] = useErrorBoundary((error) => {
    const errorMessage = 'GDPR: modal rendering error'
    window.$mediavine.newrelic.addPageAction('gdprError', { error, errorMessage })
  })
  const [state, dispatch] = useReducer(reducerCallback, initialState)
  return (
    <Context.Provider value={{ state, dispatch }}>
      {children}
    </Context.Provider>
  )
}

export { Context, StateProvider }

interface StateContext {
  state: IAppState
  dispatch: Function
}

export interface IAction {
  type: Actions
  settings?: {
    framework: Framework
    id: number
    property: ManageSettings | ManagePartners
    status: boolean
  }
  view?: Views
}

export interface IAppState {
  acceptedAcPartners: {[key: number]: boolean}
  color: string
  header: string
  language: Languages
  privacyPolicy: string
  title: string
  view: null | Views
}
